import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchTags as fetchTagsService, addTag as addTagService, deleteTag as deleteTagService, editTag as editTagService } from '@/services/tag.service';
import { RootState } from '@/state/store';
import { setPageNo, resetResponse } from '@/state/tags/slice';

interface FetchTagsResponse {
  tags: any[];
  total_count: number;
}

export const fetchTagsThunk = createAsyncThunk<
  FetchTagsResponse,
  void,
  { state: RootState }
>(
  'tags/fetchTags',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { pageNo, dataLength, sorting, filter, searchItem } = state.tags;

      return new Promise<FetchTagsResponse>((resolve, reject) => {
        fetchTagsService(
          pageNo,
          dataLength,
          sorting,
          filter,
          searchItem || '',
          (response: any) => {
            resolve({
              tags: response.data.tags || [],
              total_count: response.data.total_count || 0,
            });
          },
          (error: any) => {
            reject(rejectWithValue(error?.response?.data?.message || 'Failed to fetch tags'));
          }
        );
      });
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch tags');
    }
  }
);

export const loadMoreTagsThunk = createAsyncThunk<
  FetchTagsResponse,
  void,
  { state: RootState }
>(
  'tags/loadMoreTags',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { pageNo, dataLength, sorting, filter, searchItem } = state.tags;
      const nextPage = pageNo + 1;

      return new Promise<FetchTagsResponse>((resolve, reject) => {
        fetchTagsService(
          nextPage,
          dataLength,
          sorting,
          filter,
          searchItem || '',
          (response: any) => {
            resolve({
              tags: response.data.tags || [],
              total_count: response.data.total_count || 0,
            });
          },
          (error: any) => {
            reject(rejectWithValue(error?.response?.data?.message || 'Failed to load more tags'));
          }
        );
      });
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to load more tags');
    }
  }
);

export const addTagThunk = createAsyncThunk<
  any,
  string,
  { state: RootState }
>(
  'tags/addTag',
  async (tagName: string, { rejectWithValue }) => {
    try {
      return new Promise<any>((resolve, reject) => {
        addTagService(
          tagName,
          (response: any) => {
            const data = response?.data;
            console.log(data,"data add tag");
            resolve(data);
          },
          (error: any) => {
            reject(rejectWithValue(error?.response?.data?.message || 'Failed to add tag'));
          }
        );
      });
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to add tag');
    }
  }
);

export const deleteTagThunk = createAsyncThunk<
  string | number,
  string | number,
  { state: RootState }
>(
  'tags/deleteTag',
  async (tagId: string | number, { rejectWithValue }) => {
    try {
      return new Promise<string | number>((resolve, reject) => {
        deleteTagService(
          tagId,
          (response: any) => {
            // Return the deleted tag ID to remove from the list
            resolve(tagId);
          },
          (error: any) => {
            reject(rejectWithValue(error?.response?.data?.message || 'Failed to delete tag'));
          }
        );
      });
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to delete tag');
    }
  }
);

interface EditTagParams {
  id: string | number;
  tagName: string;
}

export const editTagThunk = createAsyncThunk<
  { tag: any; tagId: string | number },
  EditTagParams,
  { state: RootState }
>(
  'tags/editTag',
  async ({ id, tagName }: EditTagParams, { rejectWithValue, getState }) => {
    try {
      return new Promise<any>((resolve, reject) => {
        editTagService(
          id,
          tagName,
          (response: any) => {
            // Get the current tag from state to preserve other properties
            const state = getState();
            const currentTag = state.tags.tags.find((tag: any) => 
              String(tag.id) === String(id) || tag.id === id
            );
            
            // Return the updated tag data to update in state
            // Handle different response structures
            let updatedTag = response.data;
            if (response.data?.tag) {
              updatedTag = response.data.tag;
            } else if (!response.data || typeof response.data !== 'object') {
              updatedTag = {};
            }
            
            // Merge with current tag to preserve all properties, then update with new data
            // Always ensure the name is updated with the new value
            const mergedTag = {
              ...currentTag,
              ...updatedTag,
              id: updatedTag.id || currentTag?.id || id,
              name: tagName // Always use the new tagName we sent
            };
            
            // Return both the tag and the id to help with finding it in the reducer
            resolve({ tag: mergedTag, tagId: id });
          },
          (error: any) => {
            reject(rejectWithValue(error?.response?.data?.message || 'Failed to edit tag'));
          }
        );
      });
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to edit tag');
    }
  }
);

interface UpdateTagParams {
  id: string | number;
  tagData: any;
}

export const updateTagThunk = createAsyncThunk<
  { tag: any; tagId: string | number },
  UpdateTagParams,
  { state: RootState }
>(
  'tags/updateTag',
  async ({ id, tagData }: UpdateTagParams, { rejectWithValue, getState }) => {
    try {
      return new Promise<any>((resolve, reject) => {
        // Use editTag service to update tag data (supports both name and color)
        editTagService(
          id,
          tagData,
          (response: any) => {
            // Get the current tag from state to preserve other properties
            const state = getState();
            const currentTag = state.tags.tags.find((tag: any) => 
              String(tag.id) === String(id) || tag.id === id
            );
            
            // Return the updated tag data to update in state
            // Handle different response structures
            let updatedTag = response.data;
            if (response.data?.tag) {
              updatedTag = response.data.tag;
            } else if (!response.data || typeof response.data !== 'object') {
              updatedTag = {};
            }
            
            // Merge with current tag to preserve all properties, then update with new data
            const mergedTag = {
              ...currentTag,
              ...updatedTag,
              ...tagData, // Ensure the tagData we sent is included
              id: updatedTag.id || currentTag?.id || id
            };
            
            // Return both the tag and the id to help with finding it in the reducer
            resolve({ tag: mergedTag, tagId: id });
          },
          (error: any) => {
            reject(rejectWithValue(error?.response?.data?.message || 'Failed to update tag'));
          }
        );
      });
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update tag');
    }
  }
);
